import { Injectable } from '@angular/core';
import { CalendarFilterMapper, DateRange } from '@myarvato/shared/calendar';
import { FilterMetadata } from 'primeng/api';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { FilterType } from '../models';
import { CustomFilterMatchMode, FilterRecord, RawFilterRecord } from '@myarvato/shared/models';

@Injectable()
export class TreeTableFilterService {
  public buildFiltersFromTreeEvent(event: TreeTableLazyLoadEvent, dateRange?: DateRange, fieldName = 'ShippingDate'): FilterRecord {
    const normalized: FilterRecord = {};

    this.applyEventFilters(normalized, event);

    if (dateRange) {
      this.applyDateRangeFilter(normalized, dateRange, fieldName);
    }

    return normalized;
  }

  public normalizeFiltersForStorage(filters: RawFilterRecord | undefined): FilterRecord {
    const normalized: FilterRecord = {};

    if (!filters) {
      return normalized;
    }

    for (const [field, raw] of Object.entries(filters)) {
      if (raw != null) {
        const filterArray = Array.isArray(raw) ? raw : [raw];
        const hasValue = filterArray.some(
          (filter: FilterMetadata) =>
            filter.value !== null &&
            filter.value !== undefined &&
            filter.value !== '' &&
            (Array.isArray(filter.value) ? filter.value.length > 0 : true),
        );

        if (hasValue) {
          normalized[field] = filterArray;
        }
      }
    }

    return normalized;
  }

  public combineFilters(...filterSets: (FilterRecord | RawFilterRecord)[]): FilterRecord {
    const combined: FilterRecord = {};

    for (const filterSet of filterSets) {
      for (const [key, value] of Object.entries(filterSet)) {
        if (value !== undefined) {
          combined[key] = Array.isArray(value) ? value : [value];
        }
      }
    }

    return combined;
  }

  public createDateRangeFilter(dateRange: DateRange, fieldName: string): FilterRecord {
    return CalendarFilterMapper.getDateRangeFilter(dateRange, fieldName);
  }

  private applyEventFilters(normalized: FilterRecord, event: TreeTableLazyLoadEvent): void {
    const incoming = event.filters as RawFilterRecord | undefined;
    if (!incoming) {
      return;
    }

    for (const [field, raw] of Object.entries(incoming)) {
      if (raw != null) {
        const filterArray = Array.isArray(raw) ? raw : [raw];
        const hasValue = filterArray.some(
          (filter) =>
            filter.value !== null &&
            filter.value !== undefined &&
            filter.value !== '' &&
            (Array.isArray(filter.value) ? filter.value.length > 0 : true),
        );

        if (hasValue) {
          normalized[field] = filterArray;
        }
      }
    }
  }

  private applyDateRangeFilter(normalized: FilterRecord, dateRange: DateRange, fieldName: string): void {
    if (dateRange.relativeDateInfo) {
      normalized[fieldName] = [
        {
          value: dateRange,
          matchMode: 'relativeDaysFromToday',
        },
      ];
    } else {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      normalized[fieldName] = [
        {
          value: dateRange.from.toISOString(),
          matchMode: 'gte',
        },
        {
          value: toDate.toISOString(),
          matchMode: 'lte',
        },
      ];
    }
  }

  private getMatchModeForFilterType(filterType: FilterType): string {
    switch (filterType) {
      case FilterType.Date:
        return CustomFilterMatchMode.DATE_IS;
      case FilterType.Text:
      case FilterType.MultiText:
        return CustomFilterMatchMode.TEXT_IN;
      case FilterType.Select:
      case FilterType.MultiSelect:
      case FilterType.LazySelect:
      case FilterType.LazyMultiSelect:
        return CustomFilterMatchMode.IN;
      default:
        return CustomFilterMatchMode.EQUALS;
    }
  }
}
